import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { toApiError } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";

export interface BiomarkerRow {
  gene: string;
  score: number;
  rank: number;
}

export interface BiomarkerCelltypeRow {
  celltype_index: number;
  celltype_name: string;
  effect: number;
  effect_std: number;
  effect_min: number;
  effect_max: number;
  n_repeats: number;
  weight: number;
  rank: number;
}

export interface BiomarkerPathwayRow {
  celltype_name: string;
  pathway: string;
  pathway_size: number;
  effect: number;
  effect_std: number;
  n_repeats: number;
}

export interface BiomarkerResult {
  workflow_key: string;
  target_index: string;
  available_targets: string[];
  status: string;
  outcome: string;
  phases_completed: string[];
  message?: string | null;
  warnings: string[];
  input_guidance?: {
    analysis_scope?: string;
    required?: string[];
    recommended_for_stable_results?: string[];
    no_candidate_interpretation?: string;
  };
  summary: {
    biomarker_count: number;
    dataset?: string;
    split?: string;
    test_patients: number;
    bags: number;
    genes: number;
    pathways: number;
    model_metrics: Record<string, number>;
    top_biomarkers: BiomarkerRow[];
    top_celltypes: BiomarkerCelltypeRow[];
  };
  biomarkers: BiomarkerRow[];
  celltypes: BiomarkerCelltypeRow[];
  pathways: BiomarkerPathwayRow[];
  downloads: Array<{ artifact: string; filename: string }>;
}

export interface BiomarkerRequestParams {
  group_name: string;
  library_name: string;
  round?: number;
  target_index?: string;
}

export interface BiomarkerSampleResponse {
  h5ad_file_name: string;
  h5ad_file_ext: string;
  patient_col: string;
  celltype_col: string;
  label_col: string;
  positive_labels: string;
  negative_labels: string;
  h5ad_file_info: {
    file_name: string;
    file_extension: string;
    file_size: number | string;
    s3_path: string;
  };
  sample_use: boolean;
}

export interface iBiomarkerState {
  result: BiomarkerResult | null;
  isLoading: boolean;
  error: string | null;
  activeRequestKey: string | null;
}

export const getBiomarkerResultAPI = createAsyncThunk(
  "biomarker/getResult",
  async (params: BiomarkerRequestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${getDjangoEndpoint()}/workflow/biomarker/result/`,
        { round: 1, ...params },
        { withCredentials: true }
      );
      return response.data as BiomarkerResult;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to load biomarker result"
      );
    }
  }
);

export const loadBiomarkerSampleAPI = createAsyncThunk(
  "biomarker/loadSample",
  async (params: BiomarkerRequestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${getDjangoEndpoint()}/workflow/filter-callback/`,
        {
          ...params,
          round: 1,
          workflow_name: "biomarker",
          sample_use: true,
        },
        { withCredentials: true }
      );
      return response.data as BiomarkerSampleResponse;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to load biomarker sample"
      );
    }
  }
);

export const getBiomarkerParametersAPI = createAsyncThunk(
  "biomarker/getParameters",
  async (params: BiomarkerRequestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${getDjangoEndpoint()}/workflow/filter-callback/`,
        {
          ...params,
          round: 1,
          workflow_name: "biomarker",
          sample_use: false,
        },
        { withCredentials: true }
      );
      return response.data as BiomarkerSampleResponse;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to restore biomarker parameters"
      );
    }
  }
);

export const downloadBiomarkerSampleAPI = createAsyncThunk(
  "biomarker/downloadSample",
  async (params: BiomarkerRequestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${getDjangoEndpoint()}/workflow/file-callback/`,
        {
          ...params,
          round: 1,
          workflow_name: "biomarker",
          file_type: "expression",
          sample_use: true,
        },
        {
          responseType: "blob",
          withCredentials: true,
        }
      );
      return response.data as Blob;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to download biomarker sample"
      );
    }
  }
);

export const downloadBiomarkerInputAPI = createAsyncThunk(
  "biomarker/downloadInput",
  async (params: BiomarkerRequestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${getDjangoEndpoint()}/workflow/file-callback/`,
        {
          ...params,
          round: 1,
          workflow_name: "biomarker",
          file_type: "expression",
          sample_use: false,
        },
        {
          responseType: "blob",
          withCredentials: true,
        }
      );
      return response.data as Blob;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(
        apiError.response?.data?.error || apiError.message || "Failed to restore biomarker input"
      );
    }
  }
);

export const downloadBiomarkerArtifact = async (
  params: BiomarkerRequestParams & { artifact: string; filename: string }
) => {
  const response = await axiosInstance.get(
    `${getDjangoEndpoint()}/workflow/biomarker/download/`,
    {
      params: { round: 1, ...params, filename: undefined },
      responseType: "blob",
      withCredentials: true,
    }
  );
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = params.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const initialState: iBiomarkerState = {
  result: null,
  isLoading: false,
  error: null,
  activeRequestKey: null,
};

const buildBiomarkerRequestKey = (params: BiomarkerRequestParams) =>
  `${params.group_name}:${params.library_name}:${params.round ?? 1}:${params.target_index ?? ""}`;

const biomarkerSlice = createSlice({
  name: "biomarker",
  initialState,
  reducers: {
    clearBiomarkerResult: (state) => {
      state.result = null;
      state.error = null;
      state.activeRequestKey = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBiomarkerResultAPI.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.activeRequestKey = buildBiomarkerRequestKey(action.meta.arg);
      })
      .addCase(getBiomarkerResultAPI.fulfilled, (state, action) => {
        if (state.activeRequestKey !== buildBiomarkerRequestKey(action.meta.arg)) {
          return;
        }
        state.isLoading = false;
        state.result = action.payload;
      })
      .addCase(getBiomarkerResultAPI.rejected, (state, action) => {
        if (state.activeRequestKey !== buildBiomarkerRequestKey(action.meta.arg)) {
          return;
        }
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearBiomarkerResult } = biomarkerSlice.actions;
export default biomarkerSlice.reducer;
